import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, Wrench, Building, Paintbrush, Home, Building2, MessageCircle } from "lucide-react";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";
import type { LucideIcon } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

interface UseCase {
  slug: string;
  title: string;
  description: string;
  icon: LucideIcon;
  color: string;
}

const useCases: UseCase[] = [
  {
    slug: "electricien",
    title: "Electricien",
    description: "Gerez vos chantiers et tickets de caisse en temps reel",
    icon: Zap,
    color: "#F59E0B",
  },
  {
    slug: "plombier",
    title: "Plombier-Chauffagiste",
    description: "Pilotez vos equipes et centralisez vos factures",
    icon: Wrench,
    color: "#3B82F6",
  },
  {
    slug: "macon",
    title: "Macon / Entreprise generale",
    description: "Maitrisez vos couts de main d'oeuvre et le Jour Critique",
    icon: Building,
    color: "#F97316",
  },
  {
    slug: "peintre",
    title: "Peintre",
    description: "Optimisez vos marges sur des prix au m2 serres",
    icon: Paintbrush,
    color: "#8B5CF6",
  },
  {
    slug: "couvreur",
    title: "Couvreur-Charpentier",
    description: "Anticipez l'impact financier des intemperies",
    icon: Home,
    color: "#10B981",
  },
  {
    slug: "multi-corps",
    title: "Entreprise tous corps d'etat",
    description: "Vision consolidee de votre rentabilite multi-projets",
    icon: Building2,
    color: "#D4AF37",
  },
];

const UseCases = () => {
  usePageMeta({
    title: "B8ild par métier — Solutions BTP par spécialité",
    description: "Découvrez comment B8ild s'adapte à votre métier du BTP : électricien, plombier, maçon, peintre, couvreur. Gestion chantier par spécialité.",
    url: "https://b8ild.fr/use-cases",
  });

  return (
    <div className="min-h-screen bg-[#0A0A0F] overflow-x-hidden">
      <LandingHeader />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden pt-24">
        {/* Gradient mesh background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#F59E0B]/8 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
        </div>

        {/* Subtle grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="container relative z-10 mx-auto px-4 py-20">
          <div className="max-w-3xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2.5 rounded-full border border-[#F59E0B]/30 bg-[#F59E0B]/10 px-4 py-2 backdrop-blur-sm mb-8 animate-fade-up">
              <span className="text-sm font-medium text-[#F59E0B] uppercase tracking-wider">
                Cas d'usage
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="animate-fade-up [animation-delay:100ms]">
              <span className="block text-[2.5rem] sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1]">
                B8ild s'adapte a
              </span>
              <span className="block text-[2.5rem] sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mt-2 bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#F59E0B] bg-clip-text text-transparent">
                votre metier.
              </span>
            </h1>

            {/* Description */}
            <p className="text-lg sm:text-xl text-white/50 leading-relaxed max-w-2xl mx-auto mt-8 animate-fade-up [animation-delay:200ms]">
              Que vous soyez electricien, plombier, macon ou multi-corps,
              B8ild vous aide a piloter votre rentabilite au quotidien.
            </p>

            {/* CTA */}
            <div className="mt-10 animate-fade-up [animation-delay:300ms]">
              <Button
                size="lg"
                className="h-12 px-6 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold text-base gap-2.5 rounded-xl shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_4px_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_8px_30px_rgba(245,158,11,0.4)] hover:scale-[1.02] transition-all duration-300"
                asChild
              >
                <Link to="/auth?mode=signup">
                  Essayer gratuitement 14 jours
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="relative py-20 lg:py-24">
        <div className="container relative z-10 mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Grid */}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {useCases.map((useCase, index) => {
                const Icon = useCase.icon;
                return (
                  <Link
                    key={useCase.slug}
                    to={`/use-cases/${useCase.slug}`}
                    className="group relative rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.1] to-transparent animate-fade-up hover:from-white/[0.2] transition-all duration-300"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-6 h-full min-h-[220px] flex flex-col">
                      {/* Icon */}
                      <div
                        className="h-14 w-14 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
                        style={{ backgroundColor: `${useCase.color}15` }}
                      >
                        <Icon className="h-7 w-7" style={{ color: useCase.color }} />
                      </div>

                      {/* Content */}
                      <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-[#F59E0B] transition-colors">
                        {useCase.title}
                      </h3>
                      <p className="text-white/50 text-sm leading-relaxed flex-grow">
                        {useCase.description}
                      </p>

                      {/* Link */}
                      <div className="flex items-center gap-2 mt-4 text-sm text-white/50 group-hover:text-[#F59E0B] transition-colors">
                        Voir le cas d'usage
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Not Listed Section */}
      <section className="relative py-20 lg:py-24 border-t border-white/[0.05]">
        {/* Glow */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#1A365D]/20 rounded-full blur-[120px]" />
        </div>

        <div className="container relative z-10 mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-[#1A365D]/20 mb-6 animate-fade-up">
              <MessageCircle className="h-8 w-8 text-[#F59E0B]" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 animate-fade-up [animation-delay:100ms]">
              Votre metier n'est pas liste ?
            </h2>

            <p className="text-white/50 mb-8 animate-fade-up [animation-delay:200ms]">
              B8ild fonctionne pour tous les professionnels du BTP.
              Contactez-nous pour voir comment nous pouvons vous aider.
            </p>

            <Button
              variant="outline"
              size="lg"
              className="h-12 px-6 border-white/[0.1] bg-white/[0.02] text-white hover:bg-white/[0.05] hover:border-white/[0.2] font-medium rounded-xl transition-all duration-300 animate-fade-up [animation-delay:300ms]"
              asChild
            >
              <a href="mailto:contact@b8ild.fr">
                Nous contacter
                <ArrowRight className="h-4 w-4 ml-2" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default UseCases;
